import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button, CircularProgress,
  Alert, LinearProgress,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { trading } from '../services/api';

const fmt = (v) => v !== undefined && v !== null
  ? `₹${parseFloat(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  : '—';

export default function ZerodhaPortfolioPage() {
  const [portfolio, setPortfolio] = useState({ holdings: [], positions: [], status: '' });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await trading.zerodhaPortfolio();
      setPortfolio({
        holdings: res.data.holdings || [],
        positions: res.data.positions || [],
        status: res.data.status || '',
      });
    } catch { /* toast shown by interceptor */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await trading.syncZerodhaPortfolio();
      toast.success(res.data.message || 'Synced');
      load();
    } catch { /* toast shown by interceptor */ } finally { setSyncing(false); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Zerodha Portfolio</Typography>
          <Typography variant="body2" color="text.secondary">
            Live, straight from Zerodha — shows everything, even orders our local tracking missed.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={syncing ? <CircularProgress size={16} /> : <Refresh />}
          onClick={handleSync} disabled={syncing}
        >
          Sync from Zerodha
        </Button>
      </Box>

      {loading ? <LinearProgress /> : (
        <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            {portfolio.status === 'TOKEN_EXPIRED' && (
              <Alert severity="error">🔑 Zerodha token expired — go to Settings → Zerodha and reconnect.</Alert>
            )}

            {portfolio.holdings.length === 0 && portfolio.positions.length === 0 && portfolio.status === 'OK' && (
              <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                No holdings or open positions on Zerodha right now.
              </Typography>
            )}

            {portfolio.holdings.length > 0 && (
              <>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Holdings (Delivery)</Typography>
                <TableContainer sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f5f5f5' } }}>
                        <TableCell>Symbol</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Avg Price</TableCell>
                        <TableCell align="right">Last Price</TableCell>
                        <TableCell align="right">Day Change</TableCell>
                        <TableCell align="right">P&amp;L</TableCell>
                        <TableCell align="center">Product</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {portfolio.holdings.map(h => (
                        <TableRow key={h.symbol} hover sx={{ '& td': { py: 0.7 } }}>
                          <TableCell sx={{ fontWeight: 600 }}>{h.symbol}</TableCell>
                          <TableCell align="right">{h.quantity}</TableCell>
                          <TableCell align="right">₹{parseFloat(h.averagePrice).toFixed(2)}</TableCell>
                          <TableCell align="right">₹{parseFloat(h.lastPrice).toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ color: h.dayChangePct >= 0 ? '#2e7d32' : '#c62828' }}>
                            {h.dayChangePct >= 0 ? '+' : ''}{parseFloat(h.dayChangePct || 0).toFixed(2)}%
                          </TableCell>
                          <TableCell align="right" sx={{ color: h.pnl >= 0 ? '#2e7d32' : '#c62828', fontWeight: 600 }}>
                            {fmt(h.pnl)}
                          </TableCell>
                          <TableCell align="center"><Chip size="small" label={h.product} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {portfolio.positions.length > 0 && (
              <>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Open Positions (Intraday/F&amp;O)</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f5f5f5' } }}>
                        <TableCell>Symbol</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Avg Price</TableCell>
                        <TableCell align="right">Last Price</TableCell>
                        <TableCell align="right">P&amp;L</TableCell>
                        <TableCell align="center">Product</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {portfolio.positions.map(p => (
                        <TableRow key={p.symbol} hover sx={{ '& td': { py: 0.7 } }}>
                          <TableCell sx={{ fontWeight: 600 }}>{p.symbol}</TableCell>
                          <TableCell align="right">{p.quantity}</TableCell>
                          <TableCell align="right">₹{parseFloat(p.averagePrice).toFixed(2)}</TableCell>
                          <TableCell align="right">₹{parseFloat(p.lastPrice).toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ color: p.pnl >= 0 ? '#2e7d32' : '#c62828', fontWeight: 600 }}>
                            {fmt(p.pnl)}
                          </TableCell>
                          <TableCell align="center"><Chip size="small" label={p.product} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
